import { FunctionComponent, Suspense, useEffect, useState } from 'react';
import { RelayEnvironmentProvider } from 'react-relay';
import {
  RouteComponent,
  Router,
  createBrowserHistory,
} from 'react-resource-router';
import { appRoutes } from '../routes/';
import { environment as relayEnvironment } from '../services/relay';
import { NavBar } from '../components/nav-bar/NavBar';
import { Footer } from '../components/footer/Footer';
import { Initializer } from './initializer/Initializer';
import Loader from 'components/common/loader/Loader';
import { css } from '@compiled/react';
import { ResponsiveProvider } from 'contexts/Responsive';
import { FlagsProvider } from '@atlaskit/flag';
import { CommunityAnalyticsClient } from '@atlassian/community-library';
import { ENVIRONMENT, getEnvironment } from 'utils';
import { MOBILE } from '../constants/breakpoints';
import { Helmet, HelmetProvider } from 'react-helmet-async';

const history = createBrowserHistory();

export const App: FunctionComponent = () => {
  const [currentPath, setCurrentPath] = useState(history.location.pathname);

  useEffect(() => {
    CommunityAnalyticsClient.initWithPageAnalytics(
      CommunityAnalyticsClient.SUB_APP.UNIFIED_PORTABLE_PROFILE,
      null,
      getEnvironment() as ENVIRONMENT,
      {},
    );

    const unlisten = history.listen(({ location }) => {
      setCurrentPath(location.pathname);
    });

    return () => {
      unlisten();
    };
  }, []);
  // We don't want the footer on 'edit profile' view
  const shouldShowFooter = !currentPath.includes('edit');
  return (
    <div id={'app-container'} css={appContainerStyles}>
      <RelayEnvironmentProvider environment={relayEnvironment}>
        <Suspense fallback={<Loader />}>
          <ResponsiveProvider>
            <Initializer>
              <FlagsProvider>
                <HelmetProvider>
                  <Router routes={appRoutes} history={history} basePath="/user">
                    <Helmet>
                      <script
                        src={`https://${process.env.REACT_APP_ONETRUST_SCRIPT_DOMAIN}/assets/atl-onetrust-wrapper.min.js`}
                        type="text/javascript"
                        data-domain={process.env.REACT_APP_ONETRUST_GUID}
                        data-test={process.env.REACT_APP_ENVI !== 'production'}
                        data-geofence="false"
                        data-sync="true"
                      ></script>
                    </Helmet>
                    <NavBar />
                    <RouteComponent />
                  </Router>
                </HelmetProvider>
                <div css={footerSpacerStyles} />
                {shouldShowFooter && <Footer />}
              </FlagsProvider>
            </Initializer>
          </ResponsiveProvider>
        </Suspense>
      </RelayEnvironmentProvider>
    </div>
  );
};

const appContainerStyles = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  '*': {
    boxSizing: 'border-box',
  },
});

const footerSpacerStyles = css({
  height: '40px',
  [MOBILE.MEDIA_QUERY]: {
    height: '32px',
  },
});
